// src/pages/Dashboard/FeedbackQuestionCards.jsx
// Standalone Q&A feedback question cards — matches the screenshot design exactly.
// Fetches directly from /cohort/day10-summary and renders the "cards" view.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spin, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

// ── Design tokens ─────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  teal:'#0891b2',  lteal:'#ecfeff',
  purple:'#7c3aed',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

// Q12 — "Have you faced challenges?" — No = positive
const INVERTED_KEYS = new Set(['q12']);

// Q3 & Q8 = rating 1-5, Q10 = choice
const ANSWER_TYPE_OVERRIDE = { q3:'rating5', q8:'rating5', q10:'choice' };

function ratingColor(r) {
  return r >= 4 ? C.green : r === 3 ? C.amber : C.red;
}
function ratingBg(r) {
  return r >= 4 ? C.lgreen : r === 3 ? C.lamber : C.lred;
}

// Progress bar
const Bar = ({ pct, color, h = 7 }) => (
  <div style={{ flex:1, height:h, background:'#eaf1f8', borderRadius:20, overflow:'hidden' }}>
    <div style={{ height:'100%', width:`${Math.min(pct,100)}%`,
      background:color, borderRadius:20, transition:'width .5s ease' }}/>
  </div>
);

// ═════════════════════════════════════════════════════════════════
export default function FeedbackQuestionCards() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/cohort/day10-summary');
      const payload = res.data?.data ?? res.data;
      setData(payload);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Aggregate all cohorts into a single question list
  const questions = useMemo(() => {
    if (!data?.questions || !data?.cohorts) return [];

    // Only Dec'25+ cohorts — exclude Oct/Nov 2025 batch
    const DEC_2025_MS = dayjs('2025-12-01').valueOf();
    const cohorts = data.cohorts.filter(c =>
      c.cohortDate && dayjs(c.cohortDate).valueOf() >= DEC_2025_MS
    );

    return data.questions.map(q => {
      const aType = ANSWER_TYPE_OVERRIDE[q.key] || q.answerType || 'yesno';

      if (aType === 'rating5' || aType === 'integer') {
        let totalSum = 0, totalCount = 0;
        let allMins = [], allMaxs = [];
        cohorts.forEach(c => {
          const qs = c.questionSummary?.find(x => x.key === q.key);
          if (qs?.avg != null && qs?.count > 0) {
            totalSum   += qs.avg * qs.count;
            totalCount += qs.count;
          }
          if (qs?.min != null) allMins.push(qs.min);
          if (qs?.max != null) allMaxs.push(qs.max);
        });
        const avg = totalCount > 0 ? Math.round((totalSum / totalCount) * 10) / 10 : null;
        return { ...q, answerType: aType, avg, count: totalCount, responded: totalCount,
          min: allMins.length ? Math.min(...allMins) : null,
          max: allMaxs.length ? Math.max(...allMaxs) : null,
          yes:0, no:0, na:0, unanswered:0, yesPct:0, choiceCounts:{} };
      }

      if (aType === 'choice') {
        const choiceCounts = {};
        let totalResponded = 0;
        cohorts.forEach(c => {
          const qs = c.questionSummary?.find(x => x.key === q.key);
          Object.entries(qs?.choiceCounts || {}).forEach(([k, v]) => {
            choiceCounts[k] = (choiceCounts[k] || 0) + v;
            totalResponded  += v;
          });
        });
        return { ...q, answerType: 'choice', choiceCounts, responded: totalResponded,
          yes:0, no:0, na:0, unanswered:0, yesPct:0, avg:null, count:totalResponded };
      }

      // yesno
      const totals = cohorts.reduce((acc, c) => {
        const qs = c.questionSummary?.find(x => x.key === q.key);
        if (!qs) return acc;
        return {
          yes:        acc.yes        + (qs.yes || 0),
          no:         acc.no         + (qs.no  || 0),
          na:         acc.na         + (qs.na  || 0),
          responded:  acc.responded  + (qs.responded  || 0),
          unanswered: acc.unanswered + (qs.unanswered || 0),
        };
      }, { yes:0, no:0, na:0, responded:0, unanswered:0 });

      const isInv  = INVERTED_KEYS.has(q.key);
      const posPct = isInv
        ? (totals.yes + totals.no > 0
            ? Math.round((totals.no / (totals.yes + totals.no)) * 100) : 0)
        : (totals.responded > 0
            ? Math.round((totals.yes / totals.responded) * 100) : 0);

      return { ...q, answerType: 'yesno', ...totals, yesPct: posPct,
        avg:null, choiceCounts:{} };
    });
  }, [data]);

  if (loading && !data) {
    return (
      <div style={{ textAlign:'center', padding:48 }}>
        <Spin size="large"/>
        <div style={{ color:C.text3, marginTop:12, fontSize:13 }}>
          Loading feedback questions…
        </div>
      </div>
    );
  }

  if (!data || !questions.length) {
    return (
      <div style={{ textAlign:'center', padding:48, color:C.text3, fontSize:13,
        background:C.white, borderRadius:14, border:`1px solid ${C.border}` }}>
        No Q&A data available
        <Button icon={<ReloadOutlined/>} size="small" onClick={load}
          style={{ marginLeft:12, borderRadius:8 }}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* ── Section header ──────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:20 }}>📋</span>
          <span style={{ fontSize:15, fontWeight:700, color:C.text1 }}>
            Q&A Call Feedback Summary
          </span>
          <span style={{ fontSize:11, color:C.text3, background:'#eaf1f8',
            padding:'2px 8px', borderRadius:20 }}>
            Day-10 (Pratham) · Day-30 (Campus)
          </span>
        </div>
        <Button icon={<ReloadOutlined/>} size="small" onClick={load} loading={loading}
          style={{ borderRadius:8 }}>
          Refresh
        </Button>
      </div>

      {/* ── Question cards grid ─────────────────────────────────── */}
      <div style={{ display:'grid',
        gridTemplateColumns:'repeat(auto-fill, minmax(360px, 1fr))',
        gap:14 }}>
        {questions.map((q, idx) => {
          const aType  = q.answerType || 'yesno';
          const isInv  = INVERTED_KEYS.has(q.key);
          const posPct = aType === 'yesno' ? q.yesPct : 0;

          // Card accent color
          const accentColor = aType === 'rating5'
            ? (q.avg >= 4 ? C.green : q.avg >= 3 ? C.amber : q.avg ? C.red : C.text3)
            : aType === 'choice' ? C.blue
            : posPct >= 80 ? C.green : posPct >= 50 ? C.amber : C.red;

          const accentBg = aType === 'rating5'
            ? (q.avg >= 4 ? C.lgreen : q.avg >= 3 ? C.lamber : q.avg ? C.lred : C.surf)
            : aType === 'choice' ? C.lblue
            : posPct >= 80 ? C.lgreen : posPct >= 50 ? C.lamber : C.lred;

          const hasData = q.responded > 0 || (q.count != null && q.count > 0);

          return (
            <div key={q.key} style={{
              background:C.white, borderRadius:16,
              border:`1.5px solid ${hasData ? accentColor : C.border}22`,
              boxShadow:`0 2px 8px ${hasData ? accentColor : '#94a3b8'}10`,
              overflow:'hidden',
              transition:'transform .18s, box-shadow .18s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 8px 24px ${accentColor}20`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = `0 2px 8px ${accentColor}10`;
            }}>

              {/* Card header */}
              <div style={{ padding:'14px 18px', background:accentBg,
                borderBottom:`1px solid ${C.border}`,
                display:'flex', alignItems:'flex-start', gap:12 }}>
                {/* Number badge */}
                <div style={{ width:30, height:30, borderRadius:8, flexShrink:0,
                  background: hasData ? accentColor : C.text3,
                  color:'#fff', display:'flex', alignItems:'center',
                  justifyContent:'center', fontSize:12, fontWeight:800 }}>
                  {idx + 1}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text1, lineHeight:1.4 }}>
                    {q.label}
                  </div>
                  <div style={{ fontSize:11, color:C.text3, marginTop:3 }}>
                    {q.responded} responses
                    {q.unanswered > 0 && ` · ${q.unanswered} unanswered`}
                  </div>
                </div>
                {/* Right metric */}
                {hasData && (
                  <div style={{ fontSize:20, fontWeight:800, color:accentColor, flexShrink:0 }}>
                    {aType === 'rating5'
                      ? (q.avg != null ? `${q.avg}/5` : '—')
                      : aType === 'choice'
                        ? `${q.responded}`
                        : `${posPct}%`}
                  </div>
                )}
              </div>

              {/* Card body */}
              <div style={{ padding:'14px 18px' }}>
                {!hasData ? (
                  <div style={{ textAlign:'center', color:C.text3,
                    fontSize:12, padding:'12px 0' }}>
                    No responses yet
                  </div>

                ) : aType === 'rating5' ? (
                  /* ── Rating 1-5 ── */
                  <>
                    <div style={{ textAlign:'center', marginBottom:12 }}>
                      <span style={{ fontSize:38, fontWeight:900,
                        color:ratingColor(q.avg) }}>
                        {q.avg ?? '—'}
                      </span>
                      <span style={{ fontSize:18, color:C.text3, fontWeight:600 }}>/5</span>
                    </div>
                    {/* Stars */}
                    <div style={{ display:'flex', gap:4, justifyContent:'center', marginBottom:10 }}>
                      {[1,2,3,4,5].map(n => {
                        const filled     = q.avg != null && n <= Math.round(q.avg);
                        const halfFilled = q.avg != null && !filled && n - 0.5 <= q.avg;
                        const starCol    = filled || halfFilled
                          ? ratingColor(n)
                          : '#e2e8f0';
                        return (
                          <span key={n} style={{
                            fontSize:28, lineHeight:1, color:starCol,
                            filter: filled || halfFilled
                              ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' : 'none',
                          }}>
                            {halfFilled && !filled ? '½' : filled ? '★' : '☆'}
                          </span>
                        );
                      })}
                    </div>
                    <div style={{ fontSize:11, color:C.text3, textAlign:'center', marginBottom:10 }}>
                      {q.count} responses
                      {q.min != null && ` · min ${q.min} · max ${q.max}`}
                    </div>
                    <div style={{ padding:'7px 12px', borderRadius:10,
                      background:ratingBg(q.avg),
                      border:`1px solid ${ratingColor(q.avg)}33`,
                      fontSize:12, fontWeight:700,
                      color:ratingColor(q.avg), textAlign:'center' }}>
                      {q.avg >= 4 ? '😊 Comfortable'
                        : q.avg >= 3 ? '😐 Moderate'
                        : '😟 Needs support'}
                    </div>
                  </>

                ) : aType === 'choice' ? (
                  /* ── Choice (Daily / Call by Call) ── */
                  <>
                    {Object.entries(q.choiceCounts || {}).map(([opt, cnt]) => {
                      const pct    = q.responded > 0
                        ? Math.round((cnt / q.responded) * 100) : 0;
                      const optCol = opt === 'Daily' ? C.blue : C.purple;
                      return (
                        <div key={opt} style={{ display:'flex', alignItems:'center',
                          gap:8, marginBottom:9 }}>
                          <span style={{ width:90, fontSize:12, fontWeight:700,
                            color:optCol, flexShrink:0 }}>
                            {opt === 'Daily' ? '📅 Daily' : '📞 Call/Call'}
                          </span>
                          <Bar pct={pct} color={optCol}/>
                          <span style={{ fontSize:13, fontWeight:800,
                            color:optCol, minWidth:28, textAlign:'right' }}>{cnt}</span>
                          <span style={{ fontSize:11, color:C.text3,
                            minWidth:34, textAlign:'right' }}>{pct}%</span>
                        </div>
                      );
                    })}
                    <div style={{ marginTop:8, padding:'6px 12px', borderRadius:10,
                      background:C.lblue, border:`1px solid ${C.blue}33`,
                      fontSize:11, fontWeight:700, color:C.blue, textAlign:'center' }}>
                      📊 {q.responded} total responses
                    </div>
                  </>

                ) : (
                  /* ── Yes / No / NA ── */
                  <>
                    {(() => {
                      const rows = isInv
                        ? [
                            { label:'✓ No (Good)',   count:q.no,  color:C.green },
                            { label:'✗ Yes (Issue)',  count:q.yes, color:C.red   },
                            { label:'– NA',           count:q.na,  color:C.text3 },
                          ]
                        : [
                            { label:'✓ Yes', count:q.yes, color:C.green },
                            { label:'✗ No',  count:q.no,  color:C.red   },
                            { label:'– NA',  count:q.na,  color:C.text3 },
                          ];
                      return rows.map(row => {
                        const pct = q.responded > 0
                          ? Math.round((row.count / q.responded) * 100) : 0;
                        return (
                          <div key={row.label} style={{ display:'flex',
                            alignItems:'center', gap:8, marginBottom:8 }}>
                            <span style={{ width:76, fontSize:12, fontWeight:700,
                              color:row.color, flexShrink:0 }}>
                              {row.label}
                            </span>
                            <Bar pct={pct} color={row.color}/>
                            <span style={{ fontSize:13, fontWeight:800,
                              color:row.color, minWidth:28, textAlign:'right' }}>
                              {row.count}
                            </span>
                            <span style={{ fontSize:11, color:C.text3,
                              minWidth:34, textAlign:'right' }}>
                              {pct}%
                            </span>
                          </div>
                        );
                      });
                    })()}

                    {/* Signal banner */}
                    <div style={{ marginTop:10, padding:'7px 12px', borderRadius:10,
                      background:accentBg,
                      border:`1px solid ${accentColor}33`,
                      fontSize:12, fontWeight:700, color:accentColor, textAlign:'center' }}>
                      {posPct >= 80
                        ? '😊 Strong positive response'
                        : posPct >= 50
                          ? '😐 Moderate — needs follow-up'
                          : '😟 Low positivity — action required'}
                      {isInv && (
                        <span style={{ fontSize:10, fontWeight:400,
                          opacity:.7, marginLeft:6 }}>
                          (No = positive)
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}